import { useMemo } from "react";
import { STATUS, BLOCK_STYLE } from "@/lib/format";
import { CalendarDays } from "lucide-react";

export const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const addDays = (key, n) => dayKey(new Date(new Date(key + "T00:00:00").getTime() + n * 86400000));
const nightsIn = (b, k) => Math.round((new Date(b.check_out + "T00:00:00") - new Date(k + "T00:00:00")) / 86400000);

export const FLOOR_LABELS = ["Piano terra", "Primo piano", "Secondo piano", "Terzo piano", "Quarto piano"];
export const BUILDING_LABELS = { nord: "Edificio Nord", sud: "Edificio Sud" };
export const groupRooms = (rooms) => {
  const sorted = [...rooms].sort((a, b) => (a.building || "nord").localeCompare(b.building || "nord") || (a.floor ?? 0) - (b.floor ?? 0) || a.name.localeCompare(b.name));
  const out = [];
  sorted.forEach((r) => {
    const key = `${r.building || "nord"}-${r.floor ?? 0}`;
    let g = out[out.length - 1];
    if (!g || g.key !== key) { g = { key, building: r.building || "nord", floor: r.floor ?? 0, rooms: [] }; out.push(g); }
    g.rooms.push(r);
  });
  return out;
};

export const MonthGrid = ({ cursor, rooms, bookings, drag, conflict, hint, onCellClick, onGroupClick, onDragStart, onCellEnter, compact, grouped = true }) => {
  const days = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    return Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => new Date(y, m, i + 1));
  }, [cursor]);
  const todayKey = dayKey(new Date());
  const bookingAt = (roomId, k) => bookings.find((b) => b.room_id === roomId && b.status !== "cancelled" && k >= b.check_in && k < b.check_out);
  const roomW = compact ? "w-32" : "w-48";
  const cellH = compact ? "h-[56px]" : "h-[68px]";
  const groups = grouped ? groupRooms(rooms) : [{ key: "all", rooms }];
  const multiBuilding = new Set(rooms.map((r) => r.building || "nord")).size > 1;

  return (
    <div className="w-full select-none" data-testid={`month-grid-${cursor.getFullYear()}-${cursor.getMonth() + 1}`}>
      <div className="flex border-b border-border sticky top-0 bg-card z-10">
        <div className={`${roomW} shrink-0 px-3 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground border-r border-border flex items-center gap-1`}>
          <CalendarDays className="w-3.5 h-3.5" /> Camera
        </div>
        {days.map((d) => {
          const k = dayKey(d);
          const weekend = [0, 6].includes(d.getDay());
          return (
            <div key={k} className={`flex-1 min-w-0 text-center py-2.5 border-r border-border/60 ${weekend ? "bg-secondary/60" : ""} ${k === todayKey ? "bg-accent/10" : ""}`}>
              <div className="text-[11px] text-muted-foreground uppercase">{d.toLocaleDateString("it-IT", { weekday: "narrow" })}</div>
              <div className={`text-sm font-semibold ${k === todayKey ? "text-accent" : ""}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      {groups.map((g) => (
        <div key={g.key}>
          {g.key !== "all" && (
            <div data-testid={`floor-row-${g.key}`} className="flex items-center gap-2 px-3 py-1.5 bg-secondary/70 border-b border-border text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
              <span className="text-accent">{FLOOR_LABELS[g.floor]}</span>{multiBuilding && <span>· {BUILDING_LABELS[g.building]}</span>}<span className="ml-auto normal-case font-normal">{g.rooms.length} camere</span>
            </div>
          )}
          {g.rooms.map((r) => (
        <div key={r.id} className="flex border-b border-border/60 last:border-0">
          <div className={`${roomW} shrink-0 px-3 py-3 border-r border-border flex flex-col justify-center`}>
            <div className={`${compact ? "text-sm" : "text-base"} font-medium truncate`}>{r.name}</div>
            <div className="text-xs text-muted-foreground truncate">{r.type}</div>
          </div>
          {days.map((d) => {
            const k = dayKey(d);
            const b = bookingAt(r.id, k);
            const s = b ? (b.is_block ? BLOCK_STYLE : STATUS[b.status] || STATUS.confirmed) : null;
            const isStart = b && b.check_in === k;
            const isEnd = b && addDays(k, 1) === b.check_out;
            const dragging = drag && b && drag.booking.id === b.id;
            const weekend = [0, 6].includes(d.getDay());
            const free = !!hint && !b && hint.rooms.has(r.id) && k >= hint.check_in && k < hint.check_out;
            return (
              <div
                key={k}
                role="button"
                tabIndex={0}
                data-testid={`cell-${r.id}-${k}`}
                data-free-hint={free ? "true" : undefined}
                onClick={() => onCellClick(r.id, k, b)}
                onMouseEnter={() => onCellEnter(r.id, k)}
                className={`group flex-1 min-w-0 ${cellH} border-r border-border/40 relative transition-colors ${free ? "bg-emerald-100 ring-1 ring-inset ring-emerald-400" : weekend && !b ? "bg-secondary/40" : ""} ${!b ? "hover:bg-accent/10" : ""} ${drag ? "cursor-grabbing" : ""}`}
                title={b ? (b.is_block ? `Bloccata: ${b.guest_name}` : `${b.guest_name} — trascina per spostare`) : "Disponibile"}
              >
                {!b && !drag && onGroupClick && (
                  <button
                    type="button"
                    data-testid={`group-book-${r.id}-${k}`}
                    title="Prenota tutte le camere libere in questa data"
                    onClick={(e) => { e.stopPropagation(); onGroupClick(k); }}
                    className="absolute bottom-0.5 right-0.5 z-20 hidden group-hover:flex items-center justify-center w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs font-bold shadow"
                  >+</button>
                )}
                {b && (
                  <div
                    data-testid={isStart ? (b.is_block ? `roomblock-${b.id}` : `block-${b.id}`) : undefined}
                    data-conflict={dragging && conflict ? "true" : undefined}
                    onMouseDown={(e) => { if (e.button === 0 && !b.is_block) { e.preventDefault(); onDragStart(b, "move", k); } }}
                    className={`absolute inset-y-1.5 inset-x-0.5 rounded-[3px] ${b.is_block ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} ${dragging ? (conflict ? "ring-2 ring-red-600" : "opacity-60 ring-2 ring-accent") : ""}`}
                    style={{ background: dragging && conflict ? "#DC2626" : s.block }}
                  />
                )}
                {isEnd && !b.is_block && (
                  <div
                    data-testid={`resize-${b.id}`}
                    onMouseDown={(e) => { if (e.button === 0) { e.preventDefault(); e.stopPropagation(); onDragStart(b, "resize", k); } }}
                    className="absolute inset-y-1.5 right-0.5 w-2 rounded-r-[3px] cursor-ew-resize z-20 bg-black/20 hover:bg-black/40"
                  />
                )}
                {isStart && (
                  <div className="absolute inset-y-1.5 left-1.5 z-10 flex items-center pointer-events-none" style={{ width: `calc(${Math.max(1, Math.min(nightsIn(b, k), 31))} * 100% - 12px)` }}>
                    <span className="text-[11px] text-white font-semibold truncate leading-none drop-shadow-sm">{b.guest_name}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
        </div>
      ))}
      {rooms.length === 0 && <div className="p-8 text-center text-muted-foreground">Nessuna camera. Aggiungine una nella sezione Camere.</div>}
    </div>
  );
};
